import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from './supabase';

function FloatingChat() {
  var location = useLocation();
  var [expanded, setExpanded] = useState(false);
  var [position, setPosition] = useState(function() {
    return localStorage.getItem('floating_chat_pos') || 'bottom';
  });
  var [input, setInput] = useState('');
  var [messages, setMessages] = useState([]);
  var [sending, setSending] = useState(false);
  var [convoId, setConvoId] = useState(null);
  var [dragOffset, setDragOffset] = useState(0);
  var [dragging, setDragging] = useState(false);
  var dragStartY = useRef(0);
  var dragStartOffset = useRef(0);
  var panelRef = useRef(null);
  var msgsEndRef = useRef(null);
  var [toastMsg, setToastMsg] = useState(null);

  // 只在玩具箱功能页面显示
  var showPages = ['/music', '/reading', '/bluetooth'];
  var hideChat = !showPages.includes(location.pathname);

  // /music和/reading是全屏页面没有底部导航栏
  var fullscreenPages = ['/music', '/reading'];
  var isFullscreen = fullscreenPages.includes(location.pathname);

  // 获取当前活跃对话
  useEffect(function() {
    if (hideChat) return;
    supabase.from('conversations').select('id').order('updated_at', { ascending: false }).limit(1)
      .then(function(r) {
        if (r.data && r.data[0]) setConvoId(r.data[0].id);
      });
  }, [hideChat]);

  // 加载最近消息
  useEffect(function() {
    if (!convoId || hideChat) return;
    supabase.from('messages').select('id, text, sender, created_at, image_url')
      .eq('conversation_id', convoId).neq('is_active', false)
      .order('created_at', { ascending: false }).limit(8)
      .then(function(r) {
        if (r.data) setMessages(r.data.reverse());
      });
  }, [convoId, expanded, hideChat]);

  // [corvin-seg] 等回复的时候每 2.5 秒看一眼：哥哥中途说的话先上屏
  useEffect(function() {
    if (!sending || !convoId || hideChat) return;
    var segSince = new Date(Date.now() - 120000).toISOString();
    var segTimer = setInterval(function() {
      supabase.from('messages').select('id, text, sender, created_at, image_url')
        .eq('conversation_id', convoId).eq('sender', 'aiden').neq('is_active', false)
        .gt('created_at', segSince).order('created_at', { ascending: true }).limit(8)
        .then(function(r) {
          var rows = r.data || [];
          if (!rows.length) return;
          setMessages(function(prev) {
            var have = {};
            prev.forEach(function(m) { have[m.id] = true; });
            var add = rows.filter(function(m) { return !have[m.id]; });
            return add.length ? prev.concat(add) : prev;
          });
        });
    }, 2500);
    return function() { clearInterval(segTimer); };
  }, [sending, convoId, hideChat]);

  // 展开时滚到底
  useEffect(function() {
    if (expanded && msgsEndRef.current) {
      msgsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, expanded]);

  if (hideChat) return null;

  // 发送消息
  var sendMessage = async function() {
    if (!input.trim() || !convoId || sending) return;
    setSending(true);
    var text = input;
    setInput('');

    // 乐观更新
    var tmpMsg = { id: 'tmp-' + Date.now(), text: text, sender: 'zhizhi', created_at: new Date().toISOString() };
    setMessages(function(prev) { return prev.concat([tmpMsg]); });

    try {
      // 插入数据库
      await supabase.from('messages').insert([{ text: text, sender: 'zhizhi', conversation_id: convoId }]);

      // 调API
      var resp = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_id: convoId, message: text })
      });
      var data = await resp.json();

      // 刷新消息
      var fresh = await supabase.from('messages').select('id, text, sender, created_at, image_url')
        .eq('conversation_id', convoId).neq('is_active', false)
        .order('created_at', { ascending: false }).limit(8);
      if (fresh.data) setMessages(fresh.data.reverse());

      // 如果收起状态，用toast显示林晏的回复
      if (!expanded && data.reply) {
        var replyText = data.reply.length > 40 ? data.reply.substring(0, 40) + '...' : data.reply;
        setToastMsg(replyText);
        setTimeout(function() { setToastMsg(null); }, 4000);
      }
    } catch(e) {
      console.error('FloatingChat send error:', e);
    }
    setSending(false);
  };

  // 拖拽手势
  var handleTouchStart = function(e) {
    setDragging(true);
    dragStartY.current = e.touches[0].clientY;
    dragStartOffset.current = dragOffset;
  };

  var handleTouchMove = function(e) {
    if (!dragging) return;
    var diff = dragStartY.current - e.touches[0].clientY;
    if (position === 'top') diff = -diff;
    var newOffset = dragStartOffset.current + diff;
    newOffset = Math.max(0, Math.min(newOffset, window.innerHeight * 0.5));
    setDragOffset(newOffset);
  };

  var handleTouchEnd = function() {
    setDragging(false);
    // 如果拖超过100px就展开，否则收起
    if (dragOffset > 100) {
      setExpanded(true);
      setDragOffset(0);
    } else {
      setExpanded(false);
      setDragOffset(0);
    }
  };

  var togglePosition = function() {
    var newPos = position === 'bottom' ? 'top' : 'bottom';
    setPosition(newPos);
    localStorage.setItem('floating_chat_pos', newPos);
  };

  var panelHeight = expanded ? Math.min(window.innerHeight * 0.45, 360) : (dragOffset > 0 ? dragOffset + 48 : 48);
  var isTop = position === 'top';

  var panelStyle = {
    position: 'fixed',
    left: 0, right: 0,
    zIndex: 9999,
    height: panelHeight,
    transition: dragging ? 'none' : 'height 0.3s ease',
  };
  if (isTop) { panelStyle.top = 0; }
  else { panelStyle.bottom = isFullscreen ? 0 : 56; } // 56px = bottom nav height

  return (
    <>
      {/* 林晏回复toast */}
      {toastMsg && !expanded && (
        <div className={'fc-toast ' + (isTop ? 'fc-toast-top' : 'fc-toast-bottom')}>
          <span className="fc-toast-name">晏</span>
          <span className="fc-toast-text">{toastMsg}</span>
        </div>
      )}

      <div className={'fc-panel ' + (isTop ? 'fc-top' : 'fc-bottom') + (expanded ? ' fc-expanded' : '')} style={panelStyle} ref={panelRef}>
        {/* 拖拽把手 */}
        <div className="fc-handle"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={function() { if (!dragging) setExpanded(!expanded); }}
        >
          <div className="fc-handle-bar" />
        </div>

        {/* 展开时显示消息 */}
        {(expanded || dragOffset > 60) && (
          <div className="fc-messages">
            {messages.map(function(m) {
              var isMe = m.sender === 'zhizhi';
              return (
                <div key={m.id} className={'fc-msg ' + (isMe ? 'fc-msg-me' : 'fc-msg-him')}>
                  {!isMe && <span className="fc-msg-avatar">晏</span>}
                  <div className="fc-msg-bubble">{(m.text || '').substring(0, 120)}</div>
                </div>
              );
            })}
            <div ref={msgsEndRef} />
          </div>
        )}

        {/* 输入栏 */}
        <div className="fc-input-row">
          <button className="fc-pos-btn" onClick={togglePosition}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {isTop ? <polyline points="6 9 12 15 18 9"/> : <polyline points="18 15 12 9 6 15"/>}
            </svg>
          </button>
          <input
            className="fc-input"
            placeholder="跟哥哥说..."
            value={input}
            onChange={function(e) { setInput(e.target.value); }}
            onKeyDown={function(e) { if (e.key === 'Enter') sendMessage(); }}
          />
          <button className="fc-send" onClick={sendMessage} disabled={sending || !input.trim()}>
            {sending ? '·' : '→'}
          </button>
        </div>
      </div>
    </>
  );
}

export default FloatingChat;
